import React, { useState, useEffect } from 'react';
import { useSubdomain } from '../contexts/SiteContext';
import sportsService from '../services/sportsService';
import './LocationForm.css';
import './MultiSportsSelector.css';

const LocationForm = ({ onSuccess, onCancel, editingLocation = null, siteId = null }) => {
  const { createLocation, updateLocation, sites } = useSubdomain();
  const [formData, setFormData] = useState({
    site_id: siteId || null,
    name: '',
    address: {
      street: '',
      city: '',
      state: '',
      postal_code: '',
      latitude: '',
      longitude: ''
    },
    practice_schedules: [
      {
        id: 'schedule_1',
        description: '',
        target_audience: '',
        days: [],
        start_time: '',
        end_time: '',
        max_students: 10,
        is_active: true,
        sport_id: null // NUEVO: ID del deporte para este horario
      }
    ],
    monthly_cost: '',
    class_cost: '',
    instructor_info: {
      name: '',
      email: '',
      phone: '',
      biography: '',
      photo: '',
      credentials: '',
      experience: ''
    }
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [availableSports, setAvailableSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(true);

  // Cargar datos si está editando
  useEffect(() => {
    if (editingLocation) {
      setFormData({
        site_id: editingLocation.site_id || null,
        name: editingLocation.name || '',
        address: {
          street: editingLocation.address?.street || '',
          city: editingLocation.address?.city || '',
          state: editingLocation.address?.state || '',
          postal_code: editingLocation.address?.postal_code || '',
          latitude: editingLocation.address?.latitude?.toString() || '',
          longitude: editingLocation.address?.longitude?.toString() || ''
        },
        practice_schedules: editingLocation.practice_schedules?.map(schedule => ({
          ...schedule,
          sport_id: schedule.sport_id || null
        })) || [
          {
            id: 'schedule_1',
            description: '',
            target_audience: '',
            days: [],
            start_time: '',
            end_time: '',
            max_students: 10,
            is_active: true,
            sport_id: null
          }
        ],
        monthly_cost: editingLocation.monthly_cost?.toString() || '',
        class_cost: editingLocation.class_cost?.toString() || '',
        instructor_info: {
          name: editingLocation.instructor_info?.name || '',
          email: editingLocation.instructor_info?.email || '',
          phone: editingLocation.instructor_info?.phone || '',
          biography: editingLocation.instructor_info?.biography || '',
          photo: editingLocation.instructor_info?.photo || '',
          credentials: editingLocation.instructor_info?.credentials || '',
          experience: editingLocation.instructor_info?.experience?.toString() || ''
        }
      });
    }
  }, [editingLocation]);

  // Cargar sports del site seleccionado
  useEffect(() => {
    const loadSiteSports = async () => {
      if (!formData.site_id) {
        console.log('No site_id selected, clearing sports');
        setAvailableSports([]);
        return;
      }

      try {
        setSportsLoading(true);
        const site = sites.find(s => s.id === formData.site_id);
        console.log('Selected site:', site);
        
        if (site && site.sports && Array.isArray(site.sports) && site.sports.length > 0) {
          // Si el site tiene sports definidos por IDs, cargar esos deportes específicos
          console.log('Loading sports from site IDs:', site.sports);
          const allSports = await sportsService.getSportsByCategory();
          const flatSports = Object.values(allSports).flat();
          
          // Filtrar solo los deportes que están en el site
          const siteSports = flatSports.filter(sport => site.sports.includes(sport.id));
          console.log('Site-specific sports loaded:', siteSports);
          setAvailableSports(siteSports);
        } else {
          // Si el site no tiene deportes definidos, no mostrar ninguno
          console.log('Site has no sports defined');
          setAvailableSports([]);
        }
      } catch (error) {
        console.error('Error loading site sports:', error);
        setAvailableSports([]);
      } finally {
        setSportsLoading(false);
      }
    };

    loadSiteSports();
  }, [formData.site_id, sites]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleScheduleChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      practice_schedules: prev.practice_schedules.map((schedule, i) => 
        i === index ? { ...schedule, [field]: value } : schedule
      )
    }));
  };

  const addSchedule = () => {
    setFormData(prev => ({
      ...prev,
      practice_schedules: [
        ...prev.practice_schedules,
        {
          id: `schedule_${Date.now()}`,
          description: '',
          target_audience: '',
          days: [],
          start_time: '',
          end_time: '',
          max_students: 10,
          is_active: true,
          sport_id: null // NUEVO: Campo sport_id
        }
      ]
    }));
  };

  // NUEVO: Manejar cambio de deporte en horario
  const handleScheduleSportChange = (scheduleIndex, sportId) => {
    console.log('Changing sport for schedule', scheduleIndex, 'to sport ID:', sportId);
    console.log('Current formData before change:', JSON.stringify(formData.practice_schedules[scheduleIndex], null, 2));
    
    setFormData(prev => {
      const newSchedules = prev.practice_schedules.map((schedule, index) => 
        index === scheduleIndex 
          ? { ...schedule, sport_id: sportId && sportId !== '' ? sportId : null }
          : schedule
      );
      
      console.log('New schedules after change:', JSON.stringify(newSchedules, null, 2));
      console.log('Updated schedule sport_id:', newSchedules[scheduleIndex].sport_id);
      return {
        ...prev,
        practice_schedules: newSchedules
      };
    });
  };

  const removeSchedule = (index) => {
    setFormData(prev => ({
      ...prev,
      practice_schedules: prev.practice_schedules.filter((_, i) => i !== index)
    }));
  };

  const handleDayToggle = (scheduleIndex, day) => {
    const schedule = formData.practice_schedules[scheduleIndex];
    const newDays = schedule.days.includes(day)
      ? schedule.days.filter(d => d !== day)
      : [...schedule.days, day];
    
    handleScheduleChange(scheduleIndex, 'days', newDays);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.site_id || formData.site_id.trim() === '') {
      newErrors.site_id = 'El centro deportivo es requerido y debe ser válido';
    }
    if (!formData.name.trim()) newErrors.name = 'El nombre de la ubicación es requerido';
    if (!formData.address.street.trim()) newErrors['address.street'] = 'La dirección es requerida';
    if (!formData.instructor_info.name.trim()) newErrors['instructor_info.name'] = 'El nombre del instructor es requerido';

    // Validar horarios
    formData.practice_schedules.forEach((schedule, index) => {
      if (!schedule.description.trim()) {
        newErrors[`schedule_${index}_description`] = 'La descripción del horario es requerida';
      }
      if (!schedule.target_audience.trim()) {
        newErrors[`schedule_${index}_audience`] = 'El público objetivo es requerido';
      }
      if (schedule.days.length === 0) {
        newErrors[`schedule_${index}_days`] = 'Debe seleccionar al menos un día';
      }
      if (schedule.max_students <= 0) {
        newErrors[`schedule_${index}_max_students`] = 'El máximo de estudiantes debe ser mayor a 0';
      }
      // NUEVO: Validar que cada horario tenga un sport_id
      if (!schedule.sport_id) {
        newErrors[`schedule_${index}_sport`] = 'Debe seleccionar un deporte para este horario';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setErrors({}); // Limpiar errores previos
      
      if (editingLocation) {
        await updateLocation(editingLocation.id, formData);
      } else {
        await createLocation(formData);
      }
      
      onSuccess?.();
    } catch (error) {
      console.error('Error guardando location:', error);
      // Mostrar el error al usuario
      setErrors({ 
        general: error.message || 'Error al guardar la ubicación. Por favor, intenta nuevamente.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const daysOfWeek = [
    { value: 'lunes', label: 'Lunes' },
    { value: 'martes', label: 'Martes' },
    { value: 'miércoles', label: 'Miércoles' },
    { value: 'jueves', label: 'Jueves' },
    { value: 'viernes', label: 'Viernes' },
    { value: 'sábado', label: 'Sábado' },
    { value: 'domingo', label: 'Domingo' }
  ];

  return (
    <div className="location-form">
      <div className="form-header">
        <h2>{editingLocation ? 'Editar Ubicación' : 'Crear Nueva Ubicación'}</h2>
        <p className="form-description">
          {editingLocation 
            ? 'Actualiza la información de la ubicación' 
            : 'Completa la información para crear una nueva ubicación'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit} className="form-content">
        {/* Error general */}
        {errors.general && (
          <div className="error-banner">
            <span className="error-icon">⚠️</span>
            <span className="error-text">{errors.general}</span>
          </div>
        )}

        {/* Selección de Centro Deportivo */}
        <div className="form-section">
          <h3>Centro Deportivo</h3>
          <div className="form-group">
            <label htmlFor="site_id">Centro Deportivo *</label>
            <select
              id="site_id"
              value={formData.site_id}
              onChange={(e) => handleInputChange('site_id', e.target.value)}
              className={errors.site_id ? 'error' : ''}
              disabled={!!siteId} // Deshabilitar si ya se especificó el siteId
            >
              <option value="">Selecciona un centro deportivo</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.name} - {site.sports && site.sports.length > 0 ? `${site.sports.length} deportes` : 'Sin deportes'}
                </option>
              ))}
            </select>
            {errors.site_id && <span className="error-message">{errors.site_id}</span>}
          </div>
        </div>

        {/* Información Básica */}
        <div className="form-section">
          <h3>Información Básica</h3>
          
          <div className="form-group">
            <label htmlFor="name">Nombre de la Ubicación *</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={errors.name ? 'error' : ''}
              placeholder="Ej: Sede Central, Sucursal Norte, etc."
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>
        </div>

        {/* Dirección */}
        <div className="form-section">
          <h3>Dirección</h3>
          
          <div className="form-group">
            <label htmlFor="address.street">Dirección *</label>
            <input
              type="text"
              id="address.street"
              value={formData.address.street}
              onChange={(e) => handleInputChange('address.street', e.target.value)}
              className={errors['address.street'] ? 'error' : ''}
              placeholder="Calle y número"
            />
            {errors['address.street'] && <span className="error-message">{errors['address.street']}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address.city">Ciudad</label>
              <input
                type="text"
                id="address.city"
                value={formData.address.city}
                onChange={(e) => handleInputChange('address.city', e.target.value)}
                placeholder="Ciudad"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address.state">Provincia/Estado</label>
              <input
                type="text"
                id="address.state"
                value={formData.address.state}
                onChange={(e) => handleInputChange('address.state', e.target.value)}
                placeholder="Provincia/Estado"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address.postal_code">Código Postal</label>
              <input
                type="text"
                id="address.postal_code"
                value={formData.address.postal_code}
                onChange={(e) => handleInputChange('address.postal_code', e.target.value)}
                placeholder="Código Postal"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address.latitude">Latitud</label>
              <input
                type="number"
                step="any"
                id="address.latitude"
                value={formData.address.latitude}
                onChange={(e) => handleInputChange('address.latitude', e.target.value)}
                placeholder="Ej: -34.6037"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address.longitude">Longitud</label>
              <input
                type="number"
                step="any"
                id="address.longitude"
                value={formData.address.longitude}
                onChange={(e) => handleInputChange('address.longitude', e.target.value)}
                placeholder="Ej: -58.3816"
              />
            </div>
          </div>
        </div>

        {/* Horarios de Práctica */}
        <div className="form-section">
          <h3>Horarios de Práctica</h3>
          
          {formData.practice_schedules.map((schedule, index) => (
            <div key={schedule.id} className="schedule-item">
              <div className="schedule-header">
                <h4>Horario {index + 1}</h4>
                {formData.practice_schedules.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSchedule(index)}
                    className="btn btn-danger btn-sm"
                  >
                    Eliminar
                  </button>
                )}
              </div>

              <div className="form-group">
                <label>Descripción *</label>
                <input
                  type="text"
                  value={schedule.description}
                  onChange={(e) => handleScheduleChange(index, 'description', e.target.value)}
                  className={errors[`schedule_${index}_description`] ? 'error' : ''}
                  placeholder="Ej: Clases de Taekwondo para principiantes"
                />
                {errors[`schedule_${index}_description`] && (
                  <span className="error-message">{errors[`schedule_${index}_description`]}</span>
                )}
              </div>

              <div className="form-group">
                <label>Público Objetivo *</label>
                <input
                  type="text"
                  value={schedule.target_audience}
                  onChange={(e) => handleScheduleChange(index, 'target_audience', e.target.value)}
                  className={errors[`schedule_${index}_audience`] ? 'error' : ''}
                  placeholder="Ej: Niños de 6-12 años"
                />
                {errors[`schedule_${index}_audience`] && (
                  <span className="error-message">{errors[`schedule_${index}_audience`]}</span>
                )}
              </div>

              {/* Selector de deporte para este horario */}
              <div className="form-group">
                <label>Deporte *</label>
                {/* Información del deporte seleccionado */}
                {schedule.sport_id && (
                  <div style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '13px' }}>
                    <strong>Deporte seleccionado:</strong> ID {schedule.sport_id}
                    {availableSports.find(sport => sport.id === schedule.sport_id) && (
                      <span> - {availableSports.find(sport => sport.id === schedule.sport_id).label}</span>
                    )}
                  </div>
                )}
                <select
                  value={schedule.sport_id ? String(schedule.sport_id) : ''}
                  onChange={(e) => {
                    handleScheduleSportChange(index, e.target.value);
                  }}
                  className={errors[`schedule_${index}_sport`] ? 'error' : ''}
                  disabled={sportsLoading || availableSports.length === 0}
                >
                  <option value="">
                    {sportsLoading ? 'Cargando deportes...' : 
                     availableSports.length === 0 ? 'No hay deportes disponibles para este site' : 
                     'Selecciona un deporte'}
                  </option>
                  {availableSports.map(sport => (
                    <option key={sport.id} value={String(sport.id)}>
                      {sport.icon} {sport.label} (ID: {sport.id})
                    </option>
                  ))}
                </select>
                {errors[`schedule_${index}_sport`] && (
                  <span className="error-message">{errors[`schedule_${index}_sport`]}</span>
                )}
                {/* Información adicional para el usuario */}
                <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Solo se muestran los deportes disponibles en el site seleccionado
                </small>
              </div>

              <div className="form-group">
                <label>Días de la Semana *</label>
                <div className="days-selector">
                  {daysOfWeek.map(day => (
                    <label key={day.value} className="day-checkbox">
                      <input
                        type="checkbox"
                        checked={schedule.days.includes(day.value)}
                        onChange={() => handleDayToggle(index, day.value)}
                      />
                      {day.label}
                    </label>
                  ))}
                </div>
                {errors[`schedule_${index}_days`] && (
                  <span className="error-message">{errors[`schedule_${index}_days`]}</span>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Hora de Inicio</label>
                  <input
                    type="time"
                    value={schedule.start_time}
                    onChange={(e) => handleScheduleChange(index, 'start_time', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Hora de Fin</label>
                  <input
                    type="time"
                    value={schedule.end_time}
                    onChange={(e) => handleScheduleChange(index, 'end_time', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Máx. Estudiantes *</label>
                  <input
                    type="number"
                    min="1"
                    value={schedule.max_students}
                    onChange={(e) => handleScheduleChange(index, 'max_students', parseInt(e.target.value))}
                    className={errors[`schedule_${index}_max_students`] ? 'error' : ''}
                  />
                  {errors[`schedule_${index}_max_students`] && (
                    <span className="error-message">{errors[`schedule_${index}_max_students`]}</span>
                  )}
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addSchedule}
            className="btn btn-success"
          >
            + Agregar Horario
          </button>
        </div>

        {/* Costos */}
        <div className="form-section">
          <h3>Costos</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="monthly_cost">Costo Mensual</label>
              <input
                type="number"
                step="0.01"
                min="0"
                id="monthly_cost"
                value={formData.monthly_cost}
                onChange={(e) => handleInputChange('monthly_cost', e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="class_cost">Costo por Clase</label>
              <input
                type="number"
                step="0.01"
                min="0"
                id="class_cost"
                value={formData.class_cost}
                onChange={(e) => handleInputChange('class_cost', e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        {/* Información del Instructor */}
        <div className="form-section">
          <h3>Información del Instructor</h3>
          
          <div className="form-group">
            <label htmlFor="instructor_info.name">Nombre del Instructor *</label>
            <input
              type="text"
              id="instructor_info.name"
              value={formData.instructor_info.name}
              onChange={(e) => handleInputChange('instructor_info.name', e.target.value)}
              className={errors['instructor_info.name'] ? 'error' : ''}
              placeholder="Nombre completo del instructor"
            />
            {errors['instructor_info.name'] && (
              <span className="error-message">{errors['instructor_info.name']}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="instructor_info.email">Email</label>
              <input
                type="email"
                id="instructor_info.email"
                value={formData.instructor_info.email}
                onChange={(e) => handleInputChange('instructor_info.email', e.target.value)}
                placeholder="instructor@ejemplo.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="instructor_info.phone">Teléfono</label>
              <input
                type="tel"
                id="instructor_info.phone"
                value={formData.instructor_info.phone}
                onChange={(e) => handleInputChange('instructor_info.phone', e.target.value)}
                placeholder="+54 11 1234-5678"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="instructor_info.biography">Biografía</label>
            <textarea
              id="instructor_info.biography"
              value={formData.instructor_info.biography}
              onChange={(e) => handleInputChange('instructor_info.biography', e.target.value)}
              rows="3"
              placeholder="Experiencia, certificaciones, filosofía de enseñanza..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="instructor_info.photo">Foto (URL)</label>
              <input
                type="url"
                id="instructor_info.photo"
                value={formData.instructor_info.photo}
                onChange={(e) => handleInputChange('instructor_info.photo', e.target.value)}
                placeholder="https://ejemplo.com/foto.jpg"
              />
            </div>

            <div className="form-group">
              <label htmlFor="instructor_info.credentials">Credenciales</label>
              <input
                type="text"
                id="instructor_info.credentials"
                value={formData.instructor_info.credentials}
                onChange={(e) => handleInputChange('instructor_info.credentials', e.target.value)}
                placeholder="Ej: Cinturón Negro 4to Dan"
              />
            </div>

            <div className="form-group">
              <label htmlFor="instructor_info.experience">Años de Experiencia</label>
              <input
                type="number"
                min="0"
                id="instructor_info.experience"
                value={formData.instructor_info.experience}
                onChange={(e) => handleInputChange('instructor_info.experience', e.target.value)}
                placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* Botones */}
        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-outline"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Guardando...' : (editingLocation ? 'Actualizar Ubicación' : 'Crear Ubicación')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LocationForm;
